import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tabs,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { Text } = Typography;
const { TabPane } = Tabs;

type HotelProperty = {
  id: number;
  name: string;
};

type HotelRoom = {
  id: number;
  property_id: number;
  room_number: string;
};

type HousekeepingTask = {
  id: number;
  property_id: number;
  property_name?: string | null;
  room_id: number;
  room_number?: string | null;
  task_type: string;
  status: string;
  scheduled_for?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  assigned_to?: number | null;
  notes?: string | null;
};

type MaintenanceRequest = {
  id: number;
  property_id: number;
  property_name?: string | null;
  room_id: number;
  room_number?: string | null;
  priority: string;
  status: string;
  description: string;
  reported_by?: number | null;
  assigned_to?: number | null;
  reported_at?: string | null;
  resolved_at?: string | null;
};

type HousekeepingFormValues = {
  propertyId: number;
  roomId: number;
  taskType: string;
  status: string;
  scheduledFor?: Dayjs;
  startedAt?: Dayjs;
  completedAt?: Dayjs;
  assignedTo?: number;
  notes?: string;
};

type MaintenanceFormValues = {
  propertyId: number;
  roomId: number;
  priority: string;
  status: string;
  description: string;
  reportedBy?: number;
  assignedTo?: number;
  reportedAt?: Dayjs;
  resolvedAt?: Dayjs;
};

const taskTypeLabels: Record<string, string> = {
  cleaning: "Nettoyage",
  inspection: "Inspection",
  turndown: "Couverture",
  linen_change: "Changement draps",
  maintenance_followup: "Suivi maintenance",
  other: "Autre",
};

const taskStatusLabels: Record<string, string> = {
  pending: "En attente",
  assigned: "Assignee",
  in_progress: "En cours",
  completed: "Terminee",
  cancelled: "Annulee",
};

const taskStatusColors: Record<string, string> = {
  pending: "orange",
  assigned: "blue",
  in_progress: "gold",
  completed: "green",
  cancelled: "red",
};

const priorityLabels: Record<string, string> = {
  low: "Basse",
  medium: "Moyenne",
  high: "Haute",
  urgent: "Urgent",
};

const priorityColors: Record<string, string> = {
  low: "default",
  medium: "blue",
  high: "orange",
  urgent: "red",
};

const maintenanceStatusLabels: Record<string, string> = {
  open: "Ouvert",
  in_progress: "En cours",
  resolved: "Resolue",
  cancelled: "Annulee",
};

const maintenanceStatusColors: Record<string, string> = {
  open: "orange",
  in_progress: "blue",
  resolved: "green",
  cancelled: "red",
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }
  return dayjs(value).format("DD/MM/YYYY HH:mm");
};

const formatDateTimePayload = (value?: Dayjs) =>
  value ? value.format("YYYY-MM-DD HH:mm:ss") : null;

const OperationsTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [rooms, setRooms] = useState<HotelRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [housekeepingTasks, setHousekeepingTasks] = useState<HousekeepingTask[]>([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState<MaintenanceRequest[]>([]);
  const [housekeepingLoading, setHousekeepingLoading] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [housekeepingFilters, setHousekeepingFilters] = useState({
    propertyId: "all" as number | "all",
    status: "all" as string | "all",
  });
  const [maintenanceFilters, setMaintenanceFilters] = useState({
    propertyId: "all" as number | "all",
    status: "all" as string | "all",
    priority: "all" as string | "all",
  });
  const [housekeepingModalOpen, setHousekeepingModalOpen] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<HousekeepingTask | null>(null);
  const [editingRequest, setEditingRequest] = useState<MaintenanceRequest | null>(null);
  const [housekeepingForm] = Form.useForm<HousekeepingFormValues>();
  const [maintenanceForm] = Form.useForm<MaintenanceFormValues>();

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const canViewHousekeeping = Boolean(
    hasPermission?.("Hotellerie.Operations.Housekeeping.view")
  );
  const canCreateHousekeeping = Boolean(
    hasPermission?.("Hotellerie.Operations.Housekeeping.create")
  );
  const canEditHousekeeping = Boolean(
    hasPermission?.("Hotellerie.Operations.Housekeeping.edit")
  );
  const canDeleteHousekeeping = Boolean(
    hasPermission?.("Hotellerie.Operations.Housekeeping.delete")
  );
  const canViewMaintenance = Boolean(
    hasPermission?.("Hotellerie.Operations.Maintenance.view")
  );
  const canCreateMaintenance = Boolean(
    hasPermission?.("Hotellerie.Operations.Maintenance.create")
  );
  const canEditMaintenance = Boolean(
    hasPermission?.("Hotellerie.Operations.Maintenance.edit")
  );
  const canDeleteMaintenance = Boolean(
    hasPermission?.("Hotellerie.Operations.Maintenance.delete")
  );

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchRooms = useCallback(
    async (propertyId?: number) => {
      if (requiresCompanySelection) {
        return;
      }
      if (!propertyId) {
        setRooms([]);
        return;
      }
      setRoomsLoading(true);
      try {
        const params: Record<string, number> = { property_id: propertyId };
        if (selectedCompany) {
          params.company_id = selectedCompany;
        }
        const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rooms`, {
          params,
        });
        if (response.data.success) {
          setRooms(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching rooms:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des chambres"
        );
      } finally {
        setRoomsLoading(false);
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const fetchHousekeeping = useCallback(async () => {
    if (requiresCompanySelection || !canViewHousekeeping) {
      return;
    }
    setHousekeepingLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (housekeepingFilters.propertyId !== "all") {
        params.property_id = housekeepingFilters.propertyId;
      }
      if (housekeepingFilters.status !== "all") {
        params.status = housekeepingFilters.status;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/housekeeping`, {
        params,
      });
      if (response.data.success) {
        setHousekeepingTasks(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching housekeeping:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement du menage"
      );
    } finally {
      setHousekeepingLoading(false);
    }
  }, [
    canViewHousekeeping,
    housekeepingFilters.propertyId,
    housekeepingFilters.status,
    requiresCompanySelection,
    selectedCompany,
  ]);

  const fetchMaintenance = useCallback(async () => {
    if (requiresCompanySelection || !canViewMaintenance) {
      return;
    }
    setMaintenanceLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (maintenanceFilters.propertyId !== "all") {
        params.property_id = maintenanceFilters.propertyId;
      }
      if (maintenanceFilters.status !== "all") {
        params.status = maintenanceFilters.status;
      }
      if (maintenanceFilters.priority !== "all") {
        params.priority = maintenanceFilters.priority;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/maintenance`, {
        params,
      });
      if (response.data.success) {
        setMaintenanceRequests(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching maintenance:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des demandes"
      );
    } finally {
      setMaintenanceLoading(false);
    }
  }, [
    canViewMaintenance,
    maintenanceFilters.priority,
    maintenanceFilters.propertyId,
    maintenanceFilters.status,
    requiresCompanySelection,
    selectedCompany,
  ]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchHousekeeping();
  }, [fetchHousekeeping]);

  useEffect(() => {
    fetchMaintenance();
  }, [fetchMaintenance]);

  const housekeepingPropertyId = Form.useWatch("propertyId", housekeepingForm);
  const maintenancePropertyId = Form.useWatch("propertyId", maintenanceForm);

  useEffect(() => {
    if (!housekeepingModalOpen) {
      return;
    }
    if (housekeepingPropertyId) {
      fetchRooms(housekeepingPropertyId);
    } else {
      setRooms([]);
    }
  }, [fetchRooms, housekeepingModalOpen, housekeepingPropertyId]);

  useEffect(() => {
    if (!maintenanceModalOpen) {
      return;
    }
    if (maintenancePropertyId) {
      fetchRooms(maintenancePropertyId);
    } else {
      setRooms([]);
    }
  }, [fetchRooms, maintenanceModalOpen, maintenancePropertyId]);

  const openCreateHousekeeping = () => {
    setEditingTask(null);
    housekeepingForm.resetFields();
    housekeepingForm.setFieldsValue({
      propertyId:
        housekeepingFilters.propertyId !== "all"
          ? housekeepingFilters.propertyId
          : undefined,
      taskType: "cleaning",
      status: "pending",
    });
    setHousekeepingModalOpen(true);
  };

  const openEditHousekeeping = (task: HousekeepingTask) => {
    setEditingTask(task);
    housekeepingForm.setFieldsValue({
      propertyId: task.property_id,
      roomId: task.room_id,
      taskType: task.task_type,
      status: task.status,
      scheduledFor: task.scheduled_for ? dayjs(task.scheduled_for) : undefined,
      startedAt: task.started_at ? dayjs(task.started_at) : undefined,
      completedAt: task.completed_at ? dayjs(task.completed_at) : undefined,
      assignedTo: task.assigned_to || undefined,
      notes: task.notes || undefined,
    });
    setHousekeepingModalOpen(true);
  };

  const handleSaveHousekeeping = async () => {
    const values = await housekeepingForm.validateFields();
    if (!editingTask && !canCreateHousekeeping) {
      message.warning("Vous n'avez pas les droits pour creer une tache.");
      return;
    }
    if (editingTask && !canEditHousekeeping) {
      message.warning("Vous n'avez pas les droits pour modifier cette tache.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      room_id: values.roomId,
      task_type: values.taskType,
      status: values.status,
      scheduled_for: formatDateTimePayload(values.scheduledFor),
      started_at: formatDateTimePayload(values.startedAt),
      completed_at: formatDateTimePayload(values.completedAt),
      assigned_to: values.assignedTo || null,
      notes: values.notes || null,
    };

    try {
      if (editingTask) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/housekeeping/${editingTask.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Tache mise a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/housekeeping`,
          payload
        );
        if (response.data.success) {
          message.success("Tache creee.");
        }
      }
      setHousekeepingModalOpen(false);
      setEditingTask(null);
      fetchHousekeeping();
    } catch (error: any) {
      console.error("[HOTEL] Error saving housekeeping task:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde de la tache"
      );
    }
  };

  const confirmDeleteHousekeeping = (task: HousekeepingTask) => {
    if (!canDeleteHousekeeping) {
      message.warning("Vous n'avez pas les droits pour supprimer cette tache.");
      return;
    }
    Modal.confirm({
      title: "Supprimer la tache",
      content: `Confirmer la suppression de la tache ${task.room_number || ""} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/housekeeping/${task.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Tache supprimee.");
            fetchHousekeeping();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting housekeeping task:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression de la tache"
          );
        }
      },
    });
  };

  const openCreateMaintenance = () => {
    setEditingRequest(null);
    maintenanceForm.resetFields();
    maintenanceForm.setFieldsValue({
      propertyId:
        maintenanceFilters.propertyId !== "all"
          ? maintenanceFilters.propertyId
          : undefined,
      priority: "medium",
      status: "open",
    });
    setMaintenanceModalOpen(true);
  };

  const openEditMaintenance = (request: MaintenanceRequest) => {
    setEditingRequest(request);
    maintenanceForm.setFieldsValue({
      propertyId: request.property_id,
      roomId: request.room_id,
      priority: request.priority,
      status: request.status,
      description: request.description,
      reportedBy: request.reported_by || undefined,
      assignedTo: request.assigned_to || undefined,
      reportedAt: request.reported_at ? dayjs(request.reported_at) : undefined,
      resolvedAt: request.resolved_at ? dayjs(request.resolved_at) : undefined,
    });
    setMaintenanceModalOpen(true);
  };

  const handleSaveMaintenance = async () => {
    const values = await maintenanceForm.validateFields();
    if (!editingRequest && !canCreateMaintenance) {
      message.warning("Vous n'avez pas les droits pour creer une demande.");
      return;
    }
    if (editingRequest && !canEditMaintenance) {
      message.warning("Vous n'avez pas les droits pour modifier cette demande.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      room_id: values.roomId,
      priority: values.priority,
      status: values.status,
      description: values.description,
      reported_by: values.reportedBy || null,
      assigned_to: values.assignedTo || null,
      reported_at: formatDateTimePayload(values.reportedAt),
      resolved_at: formatDateTimePayload(values.resolvedAt),
    };

    try {
      if (editingRequest) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/maintenance/${editingRequest.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Demande mise a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/maintenance`,
          payload
        );
        if (response.data.success) {
          message.success("Demande creee.");
        }
      }
      setMaintenanceModalOpen(false);
      setEditingRequest(null);
      fetchMaintenance();
    } catch (error: any) {
      console.error("[HOTEL] Error saving maintenance request:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde de la demande"
      );
    }
  };

  const confirmDeleteMaintenance = (request: MaintenanceRequest) => {
    if (!canDeleteMaintenance) {
      message.warning("Vous n'avez pas les droits pour supprimer cette demande.");
      return;
    }
    Modal.confirm({
      title: "Supprimer la demande",
      content: "Confirmer la suppression de cette demande de maintenance ?",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/maintenance/${request.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Demande supprimee.");
            fetchMaintenance();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting maintenance request:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression de la demande"
          );
        }
      },
    });
  };

  const housekeepingColumns = useMemo<ColumnsType<HousekeepingTask>>(
    () => [
      {
        title: "Tache",
        key: "task",
        render: (_value, record) => (
          <Space direction="vertical" size={0}>
            <Text strong>{taskTypeLabels[record.task_type] || record.task_type}</Text>
            <Text type="secondary">{record.property_name || "-"}</Text>
          </Space>
        ),
      },
      {
        title: "Chambre",
        dataIndex: "room_number",
        key: "room_number",
        render: (value: string | null | undefined) => value || "-",
      },
      {
        title: "Planification",
        dataIndex: "scheduled_for",
        key: "scheduled_for",
        render: (value: string | null | undefined) => formatDateTime(value),
      },
      {
        title: "Statut",
        dataIndex: "status",
        key: "status",
        render: (value: string) => (
          <Tag color={taskStatusColors[value] || "default"}>
            {taskStatusLabels[value] || value}
          </Tag>
        ),
      },
      {
        title: "Assigne",
        dataIndex: "assigned_to",
        key: "assigned_to",
        render: (value: number | null | undefined) => value || "-",
      },
      {
        title: "Actions",
        key: "actions",
        render: (_value, record) => (
          <Space size={4}>
            {canEditHousekeeping ? (
              <Button type="link" onClick={() => openEditHousekeeping(record)}>
                Modifier
              </Button>
            ) : null}
            {canDeleteHousekeeping ? (
              <Button
                type="link"
                danger
                onClick={() => confirmDeleteHousekeeping(record)}
              >
                Supprimer
              </Button>
            ) : null}
          </Space>
        ),
      },
    ],
    [canDeleteHousekeeping, canEditHousekeeping]
  );

  const maintenanceColumns = useMemo<ColumnsType<MaintenanceRequest>>(
    () => [
      {
        title: "Chambre",
        dataIndex: "room_number",
        key: "room_number",
        render: (value: string | null | undefined, record) => (
          <Space direction="vertical" size={0}>
            <Text strong>{value || "-"}</Text>
            <Text type="secondary">{record.property_name || "-"}</Text>
          </Space>
        ),
      },
      {
        title: "Priorite",
        dataIndex: "priority",
        key: "priority",
        render: (value: string) => (
          <Tag color={priorityColors[value] || "default"}>
            {priorityLabels[value] || value}
          </Tag>
        ),
      },
      {
        title: "Statut",
        dataIndex: "status",
        key: "status",
        render: (value: string) => (
          <Tag color={maintenanceStatusColors[value] || "default"}>
            {maintenanceStatusLabels[value] || value}
          </Tag>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (value: string) => value || "-",
      },
      {
        title: "Signale",
        dataIndex: "reported_at",
        key: "reported_at",
        render: (value: string | null | undefined) => formatDateTime(value),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_value, record) => (
          <Space size={4}>
            {canEditMaintenance ? (
              <Button type="link" onClick={() => openEditMaintenance(record)}>
                Modifier
              </Button>
            ) : null}
            {canDeleteMaintenance ? (
              <Button
                type="link"
                danger
                onClick={() => confirmDeleteMaintenance(record)}
              >
                Supprimer
              </Button>
            ) : null}
          </Space>
        ),
      },
    ],
    [canDeleteMaintenance, canEditMaintenance]
  );

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux operations."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <>
      <Tabs defaultActiveKey="housekeeping">
        <TabPane tab="Menage" key="housekeeping">
          {canViewHousekeeping ? (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card
                title="Menage"
                extra={
                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={fetchHousekeeping}
                      loading={housekeepingLoading}
                    >
                      Rafraichir
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={openCreateHousekeeping}
                      disabled={!canCreateHousekeeping}
                    >
                      Nouvelle tache
                    </Button>
                  </Space>
                }
              >
                <Form layout="vertical">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Form.Item label="Propriete">
                        <Select
                          value={housekeepingFilters.propertyId}
                          onChange={(value) =>
                            setHousekeepingFilters((prev) => ({
                              ...prev,
                              propertyId: value,
                            }))
                          }
                          options={[
                            { value: "all", label: "Tous" },
                            ...properties.map((property) => ({
                              value: property.id,
                              label: property.name,
                            })),
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Statut">
                        <Select
                          value={housekeepingFilters.status}
                          onChange={(value) =>
                            setHousekeepingFilters((prev) => ({
                              ...prev,
                              status: value,
                            }))
                          }
                          options={[
                            { value: "all", label: "Tous" },
                            { value: "pending", label: taskStatusLabels.pending },
                            { value: "assigned", label: taskStatusLabels.assigned },
                            { value: "in_progress", label: taskStatusLabels.in_progress },
                            { value: "completed", label: taskStatusLabels.completed },
                            { value: "cancelled", label: taskStatusLabels.cancelled },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                <Table
                  columns={housekeepingColumns}
                  dataSource={housekeepingTasks}
                  rowKey="id"
                  loading={housekeepingLoading}
                  pagination={{ pageSize: 8 }}
                />
              </Card>
            </Space>
          ) : (
            <Alert
              message="Acces refuse"
              description="Vous n'avez pas acces au menage."
              type="warning"
              showIcon
            />
          )}
        </TabPane>
        <TabPane tab="Maintenance" key="maintenance">
          {canViewMaintenance ? (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card
                title="Maintenance"
                extra={
                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={fetchMaintenance}
                      loading={maintenanceLoading}
                    >
                      Rafraichir
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={openCreateMaintenance}
                      disabled={!canCreateMaintenance}
                    >
                      Nouvelle demande
                    </Button>
                  </Space>
                }
              >
                <Form layout="vertical">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Form.Item label="Propriete">
                        <Select
                          value={maintenanceFilters.propertyId}
                          onChange={(value) =>
                            setMaintenanceFilters((prev) => ({
                              ...prev,
                              propertyId: value,
                            }))
                          }
                          options={[
                            { value: "all", label: "Tous" },
                            ...properties.map((property) => ({
                              value: property.id,
                              label: property.name,
                            })),
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Statut">
                        <Select
                          value={maintenanceFilters.status}
                          onChange={(value) =>
                            setMaintenanceFilters((prev) => ({
                              ...prev,
                              status: value,
                            }))
                          }
                          options={[
                            { value: "all", label: "Tous" },
                            { value: "open", label: maintenanceStatusLabels.open },
                            { value: "in_progress", label: maintenanceStatusLabels.in_progress },
                            { value: "resolved", label: maintenanceStatusLabels.resolved },
                            { value: "cancelled", label: maintenanceStatusLabels.cancelled },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Priorite">
                        <Select
                          value={maintenanceFilters.priority}
                          onChange={(value) =>
                            setMaintenanceFilters((prev) => ({
                              ...prev,
                              priority: value,
                            }))
                          }
                          options={[
                            { value: "all", label: "Tous" },
                            { value: "low", label: priorityLabels.low },
                            { value: "medium", label: priorityLabels.medium },
                            { value: "high", label: priorityLabels.high },
                            { value: "urgent", label: priorityLabels.urgent },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                <Table
                  columns={maintenanceColumns}
                  dataSource={maintenanceRequests}
                  rowKey="id"
                  loading={maintenanceLoading}
                  pagination={{ pageSize: 8 }}
                />
              </Card>
            </Space>
          ) : (
            <Alert
              message="Acces refuse"
              description="Vous n'avez pas acces a la maintenance."
              type="warning"
              showIcon
            />
          )}
        </TabPane>
      </Tabs>

      <Modal
        title={editingTask ? "Modifier tache" : "Nouvelle tache"}
        open={housekeepingModalOpen}
        onCancel={() => {
          setHousekeepingModalOpen(false);
          setEditingTask(null);
        }}
        onOk={handleSaveHousekeeping}
        okText="Enregistrer"
      >
        <Form form={housekeepingForm} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const propertyId = housekeepingForm.getFieldValue("propertyId");
              return (
                <Form.Item
                  label="Chambre"
                  name="roomId"
                  rules={[{ required: true, message: "Chambre requise" }]}
                >
                  <Select
                    loading={roomsLoading}
                    disabled={!propertyId}
                    options={rooms.map((room) => ({
                      value: room.id,
                      label: room.room_number,
                    }))}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Type de tache" name="taskType">
                <Select
                  options={Object.keys(taskTypeLabels).map((key) => ({
                    value: key,
                    label: taskTypeLabels[key],
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Statut" name="status">
                <Select
                  options={Object.keys(taskStatusLabels).map((key) => ({
                    value: key,
                    label: taskStatusLabels[key],
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Prevue le" name="scheduledFor">
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Debut le" name="startedAt">
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Terminee le" name="completedAt">
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Assigne a" name="assignedTo">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingRequest ? "Modifier demande" : "Nouvelle demande"}
        open={maintenanceModalOpen}
        onCancel={() => {
          setMaintenanceModalOpen(false);
          setEditingRequest(null);
        }}
        onOk={handleSaveMaintenance}
        okText="Enregistrer"
      >
        <Form form={maintenanceForm} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const propertyId = maintenanceForm.getFieldValue("propertyId");
              return (
                <Form.Item
                  label="Chambre"
                  name="roomId"
                  rules={[{ required: true, message: "Chambre requise" }]}
                >
                  <Select
                    loading={roomsLoading}
                    disabled={!propertyId}
                    options={rooms.map((room) => ({
                      value: room.id,
                      label: room.room_number,
                    }))}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Priorite" name="priority">
                <Select
                  options={Object.keys(priorityLabels).map((key) => ({
                    value: key,
                    label: priorityLabels[key],
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Statut" name="status">
                <Select
                  options={Object.keys(maintenanceStatusLabels).map((key) => ({
                    value: key,
                    label: maintenanceStatusLabels[key],
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Description requise" }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Signale par" name="reportedBy">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Assigne a" name="assignedTo">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Signale le" name="reportedAt">
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Resolue le" name="resolvedAt">
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default OperationsTab;
